/**
 * API Configuration
 *
 * Centralized endpoint definitions and helpers.
 */

// Default to /api for local dev; production sets REACT_APP_API_URL
const API_BASE = process.env.REACT_APP_API_URL || "/api";
const API_TIMEOUT = 30000;
const JSON_HEADERS = { "Content-Type": "application/json" };

const endpoints = {
  auth: {
    verify: `${API_BASE}/auth/verify`,
    register: `${API_BASE}/auth/register`,
  },

  abilities: {
    me: `${API_BASE}/abilities/me`,
  },

  registry: {
    base: `${API_BASE}/registry`,
    modules: `${API_BASE}/registry/modules`,
    features: `${API_BASE}/registry/features`,
    pages: `${API_BASE}/registry/pages`,
  },

  users: {
    me: `${API_BASE}/users/me`,
    base: `${API_BASE}/users`,
    byId: (uid) => `${API_BASE}/users/${uid}`,
    suspend: (uid) => `${API_BASE}/users/${uid}/suspend`,
    unsuspend: (uid) => `${API_BASE}/users/${uid}/unsuspend`,
    invite: `${API_BASE}/users/invite`,
  },

  roles: {
    base: `${API_BASE}/roles`,
    byId: (id) => `${API_BASE}/roles/${id}`,
  },

  samples: {
    base: `${API_BASE}/samples`,
    byId: (id) => `${API_BASE}/samples/${id}`,
  },

  metrics: {
    stream: `${API_BASE}/metrics/stream`,
    snapshot: `${API_BASE}/metrics/snapshot`,
    users: `${API_BASE}/metrics/users`,
    system: `${API_BASE}/metrics/system`,
    categories: `${API_BASE}/metrics/categories`,
    registry: `${API_BASE}/metrics/registry`,
    layout: `${API_BASE}/metrics/layout`,
    saveLayout: `${API_BASE}/metrics/layout`,
  },

  inviteCodes: {
    base: `${API_BASE}/invite-codes`,
    byId: (id) => `${API_BASE}/invite-codes/${id}`,
    resend: (id) => `${API_BASE}/invite-codes/${id}/resend`,
    validate: `${API_BASE}/invite-codes/validate`,
  },

  notifications: {
    base: `${API_BASE}/notifications`,
    stream: `${API_BASE}/notifications/stream`,
    unread: `${API_BASE}/notifications/unread`,
    markRead: (id) => `${API_BASE}/notifications/${id}/read`,
    test: `${API_BASE}/notifications/test`,
  },

  events: {
    stream: `${API_BASE}/events/stream`,
    channels: `${API_BASE}/events/channels`,
    stats: `${API_BASE}/events/stats`,
  },

  notificationTemplates: {
    base: `${API_BASE}/notification-templates`,
    byId: (id) => `${API_BASE}/notification-templates/${id}`,
    duplicate: (id) => `${API_BASE}/notification-templates/${id}/duplicate`,
    preview: (id) => `${API_BASE}/notification-templates/${id}/preview`,
    test: (id) => `${API_BASE}/notification-templates/${id}/test`,
    toggle: (id) => `${API_BASE}/notification-templates/${id}/toggle`,
    metrics: (id, period) => `${API_BASE}/notification-templates/${id}/metrics?period=${period}`,
    metadata: `${API_BASE}/notification-templates/metadata`,
    stats: `${API_BASE}/notification-templates/stats`,
  },

  health: `${API_BASE}/health`,

  portals: {
    me: `${API_BASE}/portals/me`,
    base: `${API_BASE}/portals`,
    byId: (id) => `${API_BASE}/portals/${id}`,
    bySlug: (slug) => `${API_BASE}/portals/by-slug/${slug}`,
    pages: (id) => `${API_BASE}/portals/${id}/pages`,
    deletePage: (id, pageId) => `${API_BASE}/portals/${id}/pages/${pageId}`,
    translations: (id) => `${API_BASE}/portals/${id}/translations`,
    translationsByLang: (id, lang) => `${API_BASE}/portals/${id}/translations/${lang}`,
  },
};

/**
 * Build URL with query parameters.
 */
function buildUrl(baseUrl, params = {}) {
  const url = new URL(baseUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

export default endpoints;
export { API_BASE, API_TIMEOUT, JSON_HEADERS, buildUrl, endpoints };
